import { findOtherOption, otherTextKey, type FieldDef } from '@citizen-walk/shared-types';
import { extractFieldValue, type CardPrintField } from './cardLayout';
import { isAdminNoteField } from './jobConfig';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * 項目別テキスト書式の pure helper (card-print)。
 *
 * カード印刷の各項目について以下を「項目ごとに」制御する:
 *   - 項目名 (データ設定 postFields の日本語ラベル) を「項目名: 値」で表示するか (showLabel)
 *   - 最大文字数で切り詰め (maxChars、超過は …)
 *   - 改行の有無 (wrap) / 最大行数 (maxLines)
 *   - テキスト量に応じた自動調整 (fit): 固定 / 自動縮小 / 行数可変
 *
 * preview (CSS) と PDF (jspdf) の双方が同じ計算 (computeFitFontSize / effectiveMaxLines /
 * buildFieldDisplayText) を使い、見た目を一致させる。old2 `CardTemplate.tsx::planLayout` の
 * 自動サイズ調整を「項目ごとに opt-in できる手動 + 自動のハイブリッド」へ再設計したもの。
 */

export type FitMode = 'fixed' | 'shrink' | 'grow';

/** pt → mm 換算 (1pt = 1/72 inch、1 inch = 25.4mm)。 */
export const PT_TO_MM = 25.4 / 72;
/** 自動縮小の下限フォントサイズ (pt)。これ以下には縮めない (可読性確保)。 */
export const MIN_FIT_FONT_PT = 5;
/**
 * 行高 (unitless、フォントサイズに対する倍率)。preview / PDF / 行数→高さ換算で共通。
 * 1.0 = 文字ぴったり。項目の行間 (fieldGap) を 0mm にしても項目間が空いて見える leading を
 * 抑えるため 2026-06-13 に 1.2→1.05 へ詰めた。preview (CardCell) と PDF (buildCardPdf) で
 * 同一値を使い見た目を一致させる。
 * 注: 項目間の空きの主因は、各項目が 1 行内容でも最大行数ぶんの高さを確保していた点だった。
 * これは fitMode の既定を 'fixed'→'grow' (内容ぶんの高さ) に変更して解消した (下記)。
 */
export const LINE_HEIGHT = 1.05;
/**
 * 太字をストロークで擬似表現するときの線幅 (フォントサイズに対する比率)。
 * PDF (jspdf `fillThenStroke` の lineWidth) と preview (`-webkit-text-stroke` の em 幅) で
 * 同じ比率を使い、見た目を一致させる。字幅 (advance) は Regular のままなので折り返しは不変。
 */
export const BOLD_STROKE_RATIO = 0.035;
/** テキスト領域の上インセット (mm)。preview / PDF 共通 (縦オフセット一致のため)。 */
export const TEXT_TOP_INSET_MM = 2.5;
/** テキスト領域の下マージン (mm)。preview / PDF 共通。 */
export const TEXT_BOTTOM_MARGIN_MM = 1.5;

/** postFields から key→label (日本語名) のマップを作る。 */
export function buildPostFieldLabelMap(
  postFields: readonly FieldDef[] | undefined | null,
): Record<string, string> {
  const map: Record<string, string> = {};
  if (postFields) {
    for (const f of postFields) {
      if (f.key && f.label) map[f.key] = f.label;
      // 選択肢「その他」の記入内容 (`{key}__other`) は「{項目名}（その他の内容）」で見せる。
      const other = f.key ? findOtherOption(f) : null;
      if (other) map[otherTextKey(f.key)] = `${f.label || f.key}（${other.label}の内容）`;
    }
  }
  return map;
}

/** discoverExtraFields が未知キーに付ける既定ラベルの接頭辞 (showLabel では素キーに落とす)。 */
const DISCOVERED_LABEL_PREFIX = 'カスタム: ';

/**
 * 項目の表示ラベル (showLabel の「項目名」) を解決。
 * extraFields の項目は次の優先順:
 *   1. データ設定 postFields の日本語名 (labelMap)
 *   2. 組み込み / テンプレ由来の友好的ラベル (field.label。ただし discovery 既定の「カスタム: <key>」は除く)
 *   3. 素の key (fieldPath)
 * 「カスタム: address: 値」のような二重ラベルを避けつつ、組み込み「AI生成タイトル」等は活かす。
 * それ以外の source は field.label をそのまま使う。
 */
export function resolveFieldDisplayLabel(
  field: CardPrintField,
  labelMap: Record<string, string>,
): string {
  if (field.source === 'extraFields' && field.fieldPath) {
    if (labelMap[field.fieldPath]) return labelMap[field.fieldPath]!;
    if (field.label && !field.label.startsWith(DISCOVERED_LABEL_PREFIX)) return field.label;
    return field.fieldPath;
  }
  return field.label;
}

/**
 * 値中の分類コードを日本語名に変換 (M2 aiCategoryMasters 由来の codeToName マップ駆動)。
 * 値は単一コード or `、` 連結 (getByPath の配列 join) を想定し、各トークンを map で解決。
 * マップに無いトークンはそのまま残す (ハードコードせず、見つからないものは温存)。
 */
export function resolveCodeTokens(value: string, codeToName: Record<string, string>): string {
  if (!value) return value;
  return value
    .split('、')
    .map((token) => {
      const key = token.trim();
      return codeToName[key] ?? token;
    })
    .join('、');
}

/** maxChars で切り詰め (超過時は末尾に …)。0 / undefined は無制限。 */
export function applyMaxChars(text: string, maxChars: number | undefined): string {
  if (!maxChars || maxChars <= 0) return text;
  const arr = Array.from(text);
  if (arr.length <= maxChars) return text;
  return arr.slice(0, maxChars).join('') + '…';
}

/**
 * 描画用テキストを構築 (値抽出 → maxChars 切り詰め → showLabel で「ラベル: 値」prefix)。
 * 空値は '' を返す (呼び出し側で行ごとスキップ)。
 *
 * `sessionNameMap` は computed field `sessionName` の解決用 (post.sessionId → セッション名)。
 * 未指定なら sessionName 項目は空文字 (preview / PDF で同一)。
 */
export function buildFieldDisplayText(
  post: CardPrintPost,
  field: CardPrintField,
  opts: {
    adminNote: string;
    labelMap: Record<string, string>;
    codeToName?: Record<string, string>;
    sessionNameMap?: Record<string, string>;
  },
): string {
  let raw = isAdminNoteField(field)
    ? opts.adminNote
    : extractFieldValue(post, field, { sessionNameMap: opts.sessionNameMap });
  // コード→日本語名 (style.resolveCodes、M2 マスタ駆動)。maxChars/showLabel より前に解決。
  if (field.style?.resolveCodes && opts.codeToName) {
    raw = resolveCodeTokens(raw, opts.codeToName);
  }
  const trimmed = applyMaxChars(raw, field.style?.maxChars);
  if (!trimmed) return '';
  if (field.style?.showLabel) {
    // カスタム項目名 (labelText) が指定されていれば優先、無ければ自動解決ラベル。
    const custom = field.style.labelText?.trim();
    const label = custom || resolveFieldDisplayLabel(field, opts.labelMap);
    if (label) return `${label}: ${trimmed}`;
  }
  return trimmed;
}

/**
 * field の自動調整モード (既定 'grow')。
 * 2026-06-13: 既定を 'fixed'→'grow' に変更。'fixed' は内容が 1 行でも最大行数ぶんの高さを確保する
 * ため項目間が大きく空いていた。'grow' は内容ぶんの高さだけ確保 (1 行なら 1 行) し、最大行数まで
 * 折り返す/見切れない。カード間で項目の縦位置を厳密に揃えたい場合のみ項目ごとに 'fixed' を選ぶ。
 */
export function fitMode(field: CardPrintField): FitMode {
  return field.style?.fit ?? 'grow';
}

/**
 * field の実効最大行数。
 * - `wrap === false` → 1 (改行なし)
 * - `maxLines` 指定 → 1..8 にクランプ
 * - 未設定 → 既定 (title=2 / その他=2)
 */
export function effectiveMaxLines(field: CardPrintField, fallback = 2): number {
  const s = field.style;
  if (s?.wrap === false) return 1;
  if (typeof s?.maxLines === 'number' && Number.isFinite(s.maxLines) && s.maxLines > 0) {
    // 下限 1 (0<x<1 の小数が Math.floor で 0 になり全文非表示になるのを防ぐ)。
    return Math.max(1, Math.min(8, Math.floor(s.maxLines)));
  }
  return fallback;
}

/**
 * ある幅・フォントサイズで text が占める推定行数 (preview の自動縮小用)。
 *
 * - `\n` を強制改行として扱う (PDF の splitTextToSize と整合)。
 * - 半角 (ASCII / Latin-1) は全角の 0.5 幅で概算し、英数字主体テキストの過大評価を抑える
 *   (PDF は実グリフ幅で wrap するため、推定を実測へ寄せる)。
 *
 * 注: あくまで推定。最終的な行数は PDF が `splitTextToSize` 実測で決める。
 */
export function estimateLines(text: string, fontSizePt: number, areaWidthMm: number): number {
  if (!text) return 0;
  const emMm = fontSizePt * PT_TO_MM; // 全角 1 文字の幅
  if (emMm <= 0 || areaWidthMm <= 0) return 1;
  let lines = 0;
  for (const segment of text.split('\n')) {
    if (segment.length === 0) {
      lines += 1; // 空行 (連続する改行) も 1 行
      continue;
    }
    let widthMm = 0;
    for (const ch of segment) {
      widthMm += (ch.codePointAt(0)! <= 0xff ? 0.5 : 1) * emMm;
    }
    lines += Math.max(1, Math.ceil(widthMm / areaWidthMm));
  }
  return lines;
}

/**
 * 自動調整モードに応じた実効フォントサイズ (pt) を計算。
 * - 'shrink': maxLines 行に収まるよう baseFontSize から 0.5pt 刻みで縮小 (下限 MIN_FIT_FONT_PT)。
 * - 'fixed' / 'grow': baseFontSize をそのまま返す。
 */
export function computeFitFontSize(
  text: string,
  baseFontSizePt: number,
  areaWidthMm: number,
  maxLines: number,
  fit: FitMode,
): number {
  if (fit !== 'shrink') return baseFontSizePt;
  let pt = baseFontSizePt;
  while (pt > MIN_FIT_FONT_PT && estimateLines(text, pt, areaWidthMm) > maxLines) {
    pt = Math.round((pt - 0.5) * 10) / 10;
  }
  return Math.max(MIN_FIT_FONT_PT, pt);
}

/** 自動調整モードの日本語ラベル (UI 用)。 */
export const FIT_MODE_LABELS: Record<FitMode, string> = {
  fixed: '固定 (高さ一定・超過は省略)',
  shrink: '自動縮小 (フォントを縮めて収める)',
  grow: '行数可変 (内容に応じて高さ可変)',
};
